import React, { useState, useEffect, useCallback } from 'react';
import Header from '../layout/Header/Header';
import Footer from '../layout/Footer/Footer';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import './ProjectsScreen.css';

// Images used elsewhere on the site (hero banner, About Us portrait) live in the
// same folder, so they're excluded here to avoid duplicating them in the gallery.
const EXCLUDED = ['./IMG_1546.jpeg', './IMG_1546_2.jpeg'];

function importAll(r) {
  // Filter on the source keys, not the resolved URLs — production builds
  // content-hash the filenames, so URL matching would silently stop working.
  return r
    .keys()
    .filter(key => !EXCLUDED.includes(key))
    .map(r);
}

const images = importAll(
  require.context('../assets/images', false, /\.(png|jpe?g|svg)$/)
);

export default function ProjectsScreen() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isOpen = lightboxIndex !== null;

  useScrollReveal();

  const close = useCallback(() => setLightboxIndex(null), []);

  const step = useCallback(delta => {
    setLightboxIndex(current => {
      if (current === null) return current;
      return (current + delta + images.length) % images.length;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = e => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, close, step]);

  return (
    <>
      <Header />

      <main className="projects-page">
        <section className="projects-hero">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <ol>
                <li>
                  <Link to="/">
                    <span className="breadcrumb-arrow" aria-hidden="true">←</span>
                    Home
                  </Link>
                </li>
                <li aria-current="page">Projects</li>
              </ol>
            </nav>
            <h1 className="projects-title">Recent projects</h1>
            <p className="section-lead">
              A look at some of the interiors and exteriors we've refreshed
              around Lebanon and the surrounding area.
            </p>
          </div>
        </section>

        <section className="container projects-gallery-wrap">
          <div className="projects-gallery">
            {images.map((src, i) => (
              <button
                type="button"
                key={src}
                className={`project-tile ${i === 0 ? 'is-featured' : ''}`}
                onClick={() => setLightboxIndex(i)}
                data-reveal
                data-reveal-delay={i % 4}
                aria-label={`View project photo ${i + 1} larger`}
              >
                <img src={src} alt={`Project ${i + 1}`} loading="lazy" />
                <span className="project-overlay">
                  <span className="project-zoom" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5M11 8v6M8 11h6" />
                    </svg>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>

      {isOpen && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Project photo viewer"
          onClick={close}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={close}
            aria-label="Close viewer"
          >
            ×
          </button>

          <button
            type="button"
            className="lightbox-nav lightbox-prev"
            onClick={e => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous photo"
          >
            ‹
          </button>

          <img
            className="lightbox-image"
            src={images[lightboxIndex]}
            alt={`Project ${lightboxIndex + 1}`}
            onClick={e => e.stopPropagation()}
          />

          <button
            type="button"
            className="lightbox-nav lightbox-next"
            onClick={e => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next photo"
          >
            ›
          </button>

          <p className="lightbox-counter">
            {lightboxIndex + 1} / {images.length}
          </p>
        </div>
      )}

      <Footer />
    </>
  );
}
